import React from 'react';
import { DraggableAttributes } from '@dnd-kit/core';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { cn } from '@/lib/utils';
import MoveCardButton from '@/components/ui/MoveCardButton';
import { useIsMobile } from '@/hooks/layout/use-mobile';
import { useDragContext } from '@/contexts/drag/DragContext';

interface MovableCardProps {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
  isDragging?: boolean;
  dragHandleRef?: (element: HTMLElement | null) => void;
  dragListeners?: SyntheticListenerMap;
  dragAttributes?: DraggableAttributes;
  showMoveButton?: boolean;
  moveButtonVariant?: 'default' | 'glass' | 'solid';
  moveButtonSize?: 'sm' | 'md' | 'lg';
  moveButtonIconType?: 'horizontal' | 'vertical' | 'move' | 'auto';
  backgroundImage?: string;
}

const MovableCard: React.FC<MovableCardProps> = ({
  children,
  onClick,
  className,
  isDragging = false,
  dragHandleRef,
  dragListeners,
  dragAttributes,
  showMoveButton = true,
  moveButtonVariant = 'glass',
  moveButtonSize = 'sm',
  moveButtonIconType = 'vertical',
  backgroundImage
}) => {
  const isMobile = useIsMobile();
  const { isDragging: globalIsDragging } = useDragContext();

  const cardClasses = cn(
    "group relative rounded-lg overflow-hidden h-full flex flex-col",
    "transition-all duration-300 ease-in-out",
    "shadow-sm hover:shadow-md hover:shadow-amber-100/30 dark:hover:shadow-amber-900/20",
    "cursor-pointer hover:scale-[1.02] hover:translate-y-[-4px]",
    // Beautiful purple border with proper rounded corners
    "border-2 border-brandBlue/30 dark:border-brandPurple/30",
    !backgroundImage && "bg-gradient-to-br from-brandBlue/10 to-brandPurple/10",
    className
  );

  return (
    <div 
      className={cardClasses}
      onClick={onClick}
      style={{ 
        transform: 'translateZ(0)',
        backfaceVisibility: 'hidden',
        willChange: 'transform, box-shadow',
        transformStyle: 'preserve-3d',
        WebkitFontSmoothing: 'antialiased',
        ...(backgroundImage ? { aspectRatio: '16/9' } : {})
      }}
    >
      {/* Background Image (if provided) */}
      {backgroundImage && (
        <div 
          className="absolute inset-0 w-full h-full z-0"
          style={{
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            borderRadius: 'inherit'
          }}
        />
      )}
      
      {/* Dark overlay for text readability when using background image */}
      {backgroundImage && (
        <div className="absolute inset-0 bg-gradient-to-tr from-black/60 via-black/40 to-black/20 z-0" style={{ borderRadius: 'inherit' }} />
      )}
      
      {/* Glass morphism background - ensuring exact same border radius */}
      <div 
        className={cn(
          "absolute inset-0 backdrop-blur-xl backdrop-saturate-150 -z-10 will-change-transform transition-all duration-700 ease-in-out",
          backgroundImage 
            ? "bg-gradient-to-br from-black/40 via-black/20 to-black/10 group-hover:from-black/50 group-hover:via-black/30 group-hover:to-black/20" 
            : "bg-gradient-to-br from-white/40 via-white/20 to-white/5 dark:from-black/60 dark:to-black/30 group-hover:from-white/70 group-hover:via-white/40 group-hover:to-white/20 dark:group-hover:from-black/40 dark:group-hover:to-black/10"
        )}
        style={{ 
          transform: 'translateZ(0)',
          borderRadius: 'inherit' // Inherit the exact border radius from parent
        }}
      />
      
      {/* Move button in top right corner */}
      {showMoveButton && dragHandleRef && (
        <div className="absolute top-3 right-3 z-10">
          <div
            ref={dragHandleRef}
            {...dragListeners}
            {...dragAttributes}
            className="cursor-grab active:cursor-grabbing touch-none select-none"
            style={{ 
              // Ensure hover events pass through to button
              pointerEvents: 'auto'
            }}
          >
            <MoveCardButton
              variant={moveButtonVariant}
              size={moveButtonSize}
              iconType={moveButtonIconType}
              className="opacity-70 hover:opacity-100"
              onClick={(e) => {
                e.stopPropagation();
                // Drag functionality is handled by the drag handle wrapper
              }}
            />
          </div>
        </div>
      )}
      
      {/* Card content - ensuring exact same border radius */}
      <div 
        className={`${isMobile ? 'p-3 pb-4' : 'p-5 pb-6'} flex flex-col h-full relative z-1`} 
        style={{ 
          willChange: 'contents',
          borderRadius: 'inherit' // Inherit the exact border radius from parent
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default MovableCard; 